/**
 * Attempts to interpret a number written in digits or English words.
 * Returns a normalized numeric string, or null if it cannot be parsed.
 *
 * Examples:
 *  "53"                 -> "53"
 *  "22.1"               -> "22.1"
 *  "-11"                -> "-11"
 *  "minus 12.5"         -> "-12.5"
 *  "eight and a half"   -> "8.5"
 *  "fifty three"        -> "53"
 *  "half"        -> "0.5"
 *  "8 1/2"              -> "8.5"
 * sometimes the speech recognition sends two numbers together, we want the last one
* "one two three" -> "3"
* "179 4" -> "4"
* "4 179" -> "179"   
*/
export function tryToInterpretNumber(input: string): string  {
    console.log(`tryToInterpretNumber called with input: "${input}"`);  
    if (!input) return '';

    let text = input.trim().toLowerCase();

    // Replace common fraction forms with decimals
    text = text.replace(/(\d+)\s*\/\s*2\b/g, (_, n) => String(Number(n) / 2)); // e.g. "3/2" -> "1.5"
    text = text.replace(/\b1\/2\b/g, '0.5'); // "1/2" -> "0.5"
    text = text.replace(/\b1\/4\b/g, '0.25'); // "1/4" -> "0.25"
    text = text.replace(/\b3\/4\b/g, '0.75'); // "3/4" -> "0.75"

    // Already numeric
    if (/^[+-]?\d+(\.\d+)?$/.test(text)) {
        return String(Number(text));
    }

    // Split into possible number phrases (by spaces or comma)
    // e.g. "one two three" -> ["one", "two", "three"]
    // e.g. "179 4" -> ["179", "4"]
    // e.g. "eight and a half 22" -> ["eight and a half", "22"]
    // Preserve minus/negative for each phrase
    const phrases = text.match(/((minus|negative)\s+)?([a-z\d\.\s]+?)(?=(\s+(minus|negative)\s+|\s+\d+|$))/g) || [text];

    let results: string[] = [];
    for (let candidate of phrases) {
        candidate = candidate.trim();
        if (!candidate) continue;
        // Try direct parse
        if (/^[+-]?\d+(\.\d+)?$/.test(candidate)) {
            results.push(String(Number(candidate)));
            continue;
        }
        // Try word parse
        const parsed = parseWordNumber(candidate);
        if (parsed !== null && parsed !== '') {
            results.push(parsed);
        }
    }
    // Return the last valid number found
    return results.length > 0 ? results[results.length - 1] : '';
}

function parseWordNumber(text: string): string | null {
    const small: Record<string, number> = {
        zero: 0, one: 1, two: 2, three: 3, four: 4,
        five: 5, six: 6, seven: 7, eight: 8, nine: 9,
        ten: 10, eleven: 11, twelve: 12, thirteen: 13,
        fourteen: 14, fifteen: 15, sixteen: 16,
        seventeen: 17, eighteen: 18, nineteen: 19
    };
    const tens: Record<string, number> = {
        twenty: 20, thirty: 30, forty: 40,
        fifty: 50, sixty: 60, seventy: 70,
        eighty: 80, ninety: 90
    };
    let sign = 1;
    let value = 0;
    let current = 0;
    let fraction = 0;
    let inFraction = false;
    const tokens = text
        .replace(/-/g, " ")
        .split(/\s+/)
        .filter(Boolean);
    for (let i = 0; i < tokens.length; i++) {
        const t = tokens[i];
        if (t === "minus" || t === "negative") {
            sign = -1;
            continue;
        }
        if (t === "and") {
            continue;
        }
        if (t === "half") {
            fraction += 0.5;
            continue;
        }
        if (t === "point" || t === "dot") {
            inFraction = true;
            continue;
        }
        if (small[t] !== undefined) {
            if (!inFraction) current += small[t];
            else fraction += small[t] / 10;
            continue;
        }
        if (tens[t] !== undefined) {
            current += tens[t];
            continue;
        }
        if (t === "hundred") {
            current *= 100;
            continue;
        }
        if (t === "thousand") {
            value += current * 1000;
            current = 0;
            continue;
        }
        // Embedded numeric token (e.g. "12.5")
        const num = Number(t);
        if (!Number.isNaN(num)) {
            value += num;
            continue;
        }
        // Unknown token
        return null;
    }
    const result = sign * (value + current + fraction);
    return Number.isFinite(result) ? String(result) : null;
}

export default tryToInterpretNumber;